/**
 * Staff (Supervisor / Crew Leader): Pre-close warning + Close Job buttons + Available Pickups list.
 */
import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Bell, CheckCircle, Trash2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StaffJobControls({ user }) {
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closeNotes, setCloseNotes] = useState('');

  const isLead = user?.role === 'supervisor' || user?.role === 'crew_leader';

  const loadProps = async () => {
    try {
      const res = await fetch(`${API}/api/properties`, { credentials: 'include' });
      if (res.ok) {
        const list = await res.json();
        setProperties(list);
        if (!propertyId && list.length) setPropertyId(list[0].property_id);
      }
    } catch (_) { /* noop */ }
  };

  const loadAvailable = async () => {
    if (!propertyId) return;
    try {
      const res = await fetch(`${API}/api/ops/available-pickups?property_id=${propertyId}`, { credentials: 'include' });
      if (res.ok) setAvailable(await res.json());
    } catch (_) { /* noop */ }
  };

  useEffect(() => { loadProps(); }, []);
  useEffect(() => { loadAvailable(); }, [propertyId]);

  const sendPreClose = async () => {
    if (!propertyId) return toast.error('Pick a property first');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ops/pre-close-warning`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: propertyId }),
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.detail || 'Failed');
      else toast.success(`Last-call sent to ${data.tenants_notified} residents.`);
    } finally { setLoading(false); }
  };

  const closeJob = async () => {
    if (!propertyId) return toast.error('Pick a property first');
    if (!window.confirm('Close tonight\'s job? Completion notices go only to account holders whose units were verified as serviced today. Pending units stay pending.')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ops/job-close`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: propertyId, notes: closeNotes }),
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.detail || 'Failed');
      else {
        toast.success(`Job closed. ${data.eligible_account_holders} serviced account holders eligible for completion notices.`);
        setCloseNotes('');
        loadAvailable();
      }
    } catch (e) { toast.error(e.message || 'Unable to close job.'); }
    finally { setLoading(false); }
  };

  const markPicked = async (placementId) => {
    try {
      const res = await fetch(`${API}/api/ops/trash-placements/${placementId}/pickup`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Marked picked up');
        loadAvailable();
      }
    } catch (_) { /* noop */ }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-zinc-900 border-2 border-[#FFD60A] p-5">
        <h3 className="text-lg font-bold text-[#FFD60A] mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          UBER TRASH — TONIGHT'S JOB CONTROLS
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="staff-job-property-select">
              <SelectValue placeholder="Select property" />
            </SelectTrigger>
            <SelectContent>
              {properties.map((p) => (
                <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Optional notes for the close-out (e.g., 'left by Dumpster B')"
            value={closeNotes}
            onChange={(e) => setCloseNotes(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
            rows={2}
            data-testid="staff-job-close-notes"
          />
        </div>
        {isLead && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={sendPreClose}
              disabled={loading || !propertyId}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
              data-testid="staff-pre-close-btn"
            >
              <Bell className="h-4 w-4 mr-2" /> Send Last-Call Warning
            </Button>
            <Button
              onClick={closeJob}
              disabled={loading || !propertyId}
              className="bg-[#C9A962] hover:bg-[#b6964e] text-black font-bold h-auto min-h-10 whitespace-normal max-w-full"
              data-testid="staff-close-job-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2 shrink-0" /> Close Job & Notify Serviced Units
            </Button>
          </div>
        )}
      </Card>

      <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="staff-available-pickups">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-[#FFD60A]" /> Available Pickups (residents checked in)
          </h3>
          <Badge className="bg-[#FFD60A] text-black">{available.length}</Badge>
        </div>
        {available.length === 0 ? (
          <p className="text-sm text-zinc-400 py-4 text-center">No residents have placed trash for this property yet.</p>
        ) : (
          <ul className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {available.map((p) => (
              <li
                key={p.placement_id}
                className="flex items-center justify-between bg-zinc-800 border border-zinc-700 rounded-lg p-3"
                data-testid={`staff-pickup-row-${p.placement_id}`}
              >
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-[#FFD60A]" />
                  <div>
                    <div className="text-sm font-bold text-white">
                      Unit {p.unit_number} {p.building_number ? `· Bldg ${p.building_number}` : ''}
                    </div>
                    <div className="text-xs text-zinc-400">
                      {p.tenant_name} · {new Date(p.placed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {p.placement_status === 'late' && <span className="text-amber-400 ml-2">(late)</span>}
                    </div>
                    {p.notes && <div className="text-xs text-zinc-500 mt-1">"{p.notes}"</div>}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => markPicked(p.placement_id)}
                  data-testid={`staff-pickup-mark-${p.placement_id}`}
                >
                  Mark Picked Up
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
